import React, { useState, useEffect, useRef } from 'react';
import { Link } from "react-router-dom";
import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import useApiManager from '../../utils/Api/apiManager';
import { useNavigate } from "react-router-dom";
import { TaskStatus, TaskPriority } from '../../utils/enums'
import { format } from 'date-fns';

const TasksDataGrid = ({ filters, selectedRows, onSelectionModelChange, triggerFetch, setTriggerFetch }) => {
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sortModel, setSortModel] = useState([]);
    const navigate = useNavigate();
    const { post } = useApiManager();
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 50,
    });
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [filterParams, setFilterParams] = useState([]);

    const requestIdRef = useRef(0);

    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.5 },
        {
            field: 'subject',
            headerName: 'Subject',
            flex: 1,
            renderCell: (params) => {
                const displayName = params.value?.trim() || "(N/A)"; // fallback if null, undefined, or empty
                return (
                    <Link
                        to={`/tasks/${params.row.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit' }}
                    >
                        {displayName}
                    </Link>
                );
            },
        },
        { field: 'dueDate', headerName: 'Due Date', flex: 1 },
        { field: 'status', headerName: 'Status', flex: 1 },
        { field: 'priority', headerName: 'Priority', flex: 1 },
        {
            field: 'relatedTo',
            headerName: 'Related To',
            width: 200,
            renderCell: (params) => {
                const { row } = params; // Get the full row data
                const basePath = row.entityType === 0 ? "/leads" : "/contacts";
                return (
                    <Link to={`${basePath}/${row.relatedToId}`} onClick={(event) => event.stopPropagation()}>
                        {row.relatedTo}
                    </Link>
                );
            }
        },
        { field: 'taskOwner', headerName: 'Task Owner', flex: 1 },
    ];

    const transformData = (data) => {
        return data.map(item => ({
            id: item.id,
            subject: item.subject,
            dueDate: item.dueDate ? format(new Date(item.dueDate), 'MMMM do yyyy') : null,
            status: TaskStatus?.[item.status]?.label || '',
            priority: TaskPriority?.[item.priority]?.label || '',
            relatedTo: item.leadName, // Keep plain text here
            relatedToId: item.leadId, // Pass ID separately for linking
            entityType: item.entityType, // Keep entityType for logic
            taskOwner: item.ownerName
        }));
    };

    useEffect(() => {
        setFilterParams(filters);
    }, [filters]);

    useEffect(() => {
        const fetchAndSetData = async () => {
            setLoading(true);

            const currentRequestId = ++requestIdRef.current; // Track request order

            try {
                const sortField = sortModel.length > 0 ? sortModel[0].field : 'createdAt'; // Default to 'firstName'
                const sortDescending = sortModel.length > 0 ? sortModel[0].sort === 'desc' : true;

                const data = await post('/tasks/paged', {
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    filters: filterParams,
                    sortField: sortField,
                    sortDescending: sortDescending
                });
                // Only update if this is the latest request
                if (currentRequestId === requestIdRef.current) {
                    setRows(transformData(data.items));
                    setRowCount(data.totalCount);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                if (currentRequestId === requestIdRef.current) {
                    setLoading(false);
                    if (setTriggerFetch) {
                        setTriggerFetch(false);
                    }
                }
            }
        };

        fetchAndSetData();
    }, [paginationModel, filterParams, sortModel, triggerFetch]);

    const handleRowClick = (params) => {
        navigate(`/tasks/${params.id}`);
    };

    return (
        <DataGrid
            rows={rows}
            columns={columns}
            rowCount={rowCount}
            paginationMode="server"
            sortingMode="server"
            columnVisibilityModel={{
                id: false,
            }}
            getRowId={(row) => row.id}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[50, 75, 100]}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            filterMode="server"
            onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
            }}
            checkboxSelection
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={onSelectionModelChange}
            disableRowSelectionOnClick
            loading={loading}
            onRowClick={handleRowClick}
            sx={{
                height: '100%', // Fills the parent container
                '& .MuiDataGrid-virtualScroller': {
                    overflow: 'auto', // Ensures scrollbars appear when necessary
                },
            }}
        />
    );
};

export default TasksDataGrid;
